import type { InconsistenciesQuery } from 'generated/resources/graphql'
import {
  InconsistenciesDocument,
  TimeInsightsEntitiesInconsistencyStateEnum,
  TimeInsightsValueObjectsInconsistencyTypeEnum,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { PopoverTypeToShow } from 'modules/attendance/lib/types'

type TimeInsightsMetadataType =
  InconsistenciesQuery['timeInsights']['inconsistenciesConnection']['nodes'][number]['metadata']

export interface ClockInInconsistencyType {
  inconsistencyType: TimeInsightsValueObjectsInconsistencyTypeEnum
  workedHour: number
  duration: number
  id: string
  attendanceShiftId: string | null
}

export interface ClockInInconsistencies {
  inconsistencyTypes: ClockInInconsistencyType[]
  total: number
}

export interface TTInconsistencies {
  [key: string]: number
}

export type Inconsistencies = ClockInInconsistencies | TTInconsistencies

export const isClockInInconsistencies = (
  item?: Inconsistencies
): item is ClockInInconsistencies => {
  return !!item && 'inconsistencyTypes' in item
}

export const isTTInconsistencies = (item?: Inconsistencies): item is TTInconsistencies => {
  return !!item && 'missing_clock_in' in item
}

export const shiftWithInconsistency = (
  inconsistencies: Inconsistencies,
  shiftId: string
): boolean => {
  if (!isClockInInconsistencies(inconsistencies)) return false

  return inconsistencies.inconsistencyTypes.some((i) => i.attendanceShiftId === shiftId)
}

const groupInconsistenciesByDate = (
  id: string,
  userStats: Map<string | number, Inconsistencies>,
  date: string,
  inconsistencyType: TimeInsightsValueObjectsInconsistencyTypeEnum,
  metadata: TimeInsightsMetadataType | undefined | null,
  attendanceShiftId: string | null | undefined = null
) => {
  const userStatsInDate = userStats.get(date)

  if (userStatsInDate && !isClockInInconsistencies(userStatsInDate)) return userStats

  const workedHour = (metadata?.minimalWorkedMinutes ?? 0) / 60
  const duration = metadata?.requiredBreakMinutes ?? 0

  const newInconsistency = {
    inconsistencyType,
    id,
    workedHour,
    duration,
    attendanceShiftId,
  }

  const inconsistencies = (userStatsInDate?.inconsistencyTypes ?? []).concat([newInconsistency])
  const total = inconsistencies.length

  userStats.set(date, { inconsistencyTypes: inconsistencies, total })

  return userStats
}

const groupInconsistenciesByEmployeeId = (
  userStats: Map<string | number, Inconsistencies>,
  employeeId: string,
  inconsistencyType: TimeInsightsValueObjectsInconsistencyTypeEnum
) => {
  if (!userStats.has(employeeId)) {
    userStats.set(employeeId, {
      missing_clock_in: 0,
      missing_clock_out: 0,
      missing_break_time: 0,
      insufficient_break_duration: 0,
      total: 0,
    })
  }

  const employeeStats = userStats.get(employeeId)

  if (!!employeeStats && isTTInconsistencies(employeeStats)) {
    if (typeof employeeStats[inconsistencyType] === 'number') {
      employeeStats[inconsistencyType]++
    }
    if (typeof employeeStats.total === 'number') {
      employeeStats.total++
    }
  }

  return userStats
}

export const useShiftInconsistencies = ({
  dataType,
  employeeIds,
  startOn,
  endOn,
  state,
}: {
  dataType: string
  employeeIds?: string[]
  startOn: string
  endOn: string
  state: TimeInsightsEntitiesInconsistencyStateEnum
}) => {
  const isTimesheetPage = dataType === PopoverTypeToShow.Info
  const {
    data,
    loading,
    refetch: refetchInconsistencies,
  } = useQuery(InconsistenciesDocument, {
    variables: {
      employeeIds,
      startOn,
      endOn,
      state,
    },
  })

  const nodes = data?.timeInsights?.inconsistenciesConnection.nodes ?? []

  // userStats is needed only in timesheet and tt tables
  // currently the nodes are used by the filters from tt page
  let userStats = new Map<string | number, Inconsistencies>()

  if (dataType) {
    nodes.forEach((node) => {
      const { id, date, inconsistencyType, employeeId, metadata, attendanceShiftId } = node

      userStats = isTimesheetPage
        ? groupInconsistenciesByDate(
            id,
            userStats,
            date,
            inconsistencyType,
            metadata,
            attendanceShiftId
          )
        : groupInconsistenciesByEmployeeId(userStats, employeeId, inconsistencyType)
    })
  }

  return {
    loading,
    inconsistencies: userStats,
    data: nodes,
    refetchInconsistencies,
  }
}
